import { useContext, useEffect, useMemo } from "react"

import { OverlayContext } from "shared/context/overlay/OverlayContext"
import { Overlay } from "shared/context/overlay/types"
import { useOverlayActions } from "shared/context/overlay/useOverlayActions"
import { difference, last } from "shared/lib/arrayUtils"
import { useEvent } from "shared/lib/useEvent"
import usePrevious from "shared/lib/usePrevious"

export function useOverlays(callbacks?: {
  onOverlayOpen?: (overlay: Overlay, previousOverlay: Overlay | null) => void
  onOverlayClose?: (overlay: Overlay, nextOverlay: Overlay | null) => void
}) {
  const { state } = useContext(OverlayContext)

  const { currentOverlay, overlays } = useMemo(
    () => ({
      currentOverlay: state.overlays[state.overlays.length - 1] || null,
      overlays: state.overlays
    }),
    [state]
  )

  const previousOverlays = usePrevious(overlays)

  const { closeOverlayByID, openOverlay, closeAllOverlays } =
    useOverlayActions()

  // Two same-type overlays on the stack must not both close
  const closeCurrentOverlay = useEvent(() => {
    if (currentOverlay) {
      closeOverlayByID({ id: currentOverlay.id })
    }
  })

  useEffect(() => {
    const newOverlays = difference(overlays, previousOverlays || [])
    newOverlays.forEach(newOverlay =>
      callbacks?.onOverlayOpen?.(newOverlay, last(previousOverlays) || null)
    )

    const removedOverlays = difference(previousOverlays || [], overlays)
    removedOverlays.forEach(removedOverlay =>
      callbacks?.onOverlayClose?.(removedOverlay, last(overlays) || null)
    )
  }, [overlays])

  return {
    currentOverlay,
    overlays,
    closeCurrentOverlay,
    openOverlay,
    closeAllOverlays
  }
}
